import { useCallback, useMemo, useState } from 'react';
import type {
  BBox,
  DisplacementComponent,
  DrawMode,
  IndexType,
  LayerMode,
  LngLat,
  MaterialCategory,
  PolygonTimeSeriesResponse,
  RadarPoint,
  RGBComposition,
  Scene,
} from '../types';

export interface AnalysisFilters {
  bbox: BBox;
  startDate: string;
  endDate: string;
  maxCloudCover: number;
  indexType: IndexType;
  material: MaterialCategory;
  rgbComposition: RGBComposition;
  displacementComponent: DisplacementComponent;
  decompose: boolean;
  interferogramPairIndex: number;
}

const defaultBBox: BBox = [-68.92928197168484, -22.41492573924971, -68.87345962710025, -22.35667054862041];

export const defaultFilters: AnalysisFilters = {
  bbox: defaultBBox,
  startDate: '2023-01-01',
  endDate: '2024-12-31',
  maxCloudCover: 20,
  indexType: 'MINERAL',
  material: 'FERROUS',
  rgbComposition: 'MINERAL',
  displacementComponent: 'VERTICAL',
  decompose: true,
  interferogramPairIndex: 0,
};

export interface AppStateShape {
  filters: AnalysisFilters;
  setFilters: (patch: Partial<AnalysisFilters>) => void;

  scenes: Scene[];
  setScenes: (s: Scene[]) => void;

  layer: LayerMode;
  setLayer: (l: LayerMode) => void;

  layerVisible: boolean;
  setLayerVisible: (v: boolean) => void;

  layerOpacity: number;
  setLayerOpacity: (v: number) => void;

  layerContrast: number;
  setLayerContrast: (v: number) => void;

  layerSaturation: number;
  setLayerSaturation: (v: number) => void;

  layerBrightness: number;
  setLayerBrightness: (v: number) => void;

  layerHue: number;
  setLayerHue: (v: number) => void;

  colorThresholdCmYr: number;
  setColorThresholdCmYr: (v: number) => void;

  colorMaxCmYr: number;
  setColorMaxCmYr: (v: number) => void;

  filterBelowThreshold: boolean;
  setFilterBelowThreshold: (v: boolean) => void;

  radarPoints: RadarPoint[];
  setRadarPoints: (p: RadarPoint[]) => void;

  radarPointsVisible: boolean;
  setRadarPointsVisible: (v: boolean) => void;

  selectedPointId: string | null;
  setSelectedPointId: (id: string | null) => void;

  // Polygon drawing state
  drawMode: DrawMode;
  setDrawMode: (m: DrawMode) => void;

  drawnPolygon: LngLat[] | null;
  setDrawnPolygon: (p: LngLat[] | null) => void;

  polygonResult: PolygonTimeSeriesResponse | null;
  setPolygonResult: (r: PolygonTimeSeriesResponse | null) => void;

  highlightedPointIds: Set<string>;
  setHighlightedPointIds: (ids: Set<string>) => void;

  busy: Record<string, boolean>;
  setBusy: (key: string, v: boolean) => void;

  error: string | null;
  setError: (e: string | null) => void;
}

export function useAppState(): AppStateShape {
  const [filters, setFiltersRaw] = useState<AnalysisFilters>(defaultFilters);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [layer, setLayer] = useState<LayerMode>({ kind: 'NONE' });
  const [layerVisible, setLayerVisible] = useState(true);
  const [layerOpacity, setLayerOpacity] = useState(0.75);
  const [layerContrast, setLayerContrast] = useState(115);
  const [layerSaturation, setLayerSaturation] = useState(125);
  const [layerBrightness, setLayerBrightness] = useState(100);
  const [layerHue, setLayerHue] = useState(0);
  const [colorThresholdCmYr, setColorThresholdCmYr] = useState(3.45);
  const [colorMaxCmYr, setColorMaxCmYr] = useState(12);
  const [filterBelowThreshold, setFilterBelowThreshold] = useState(false);
  const [radarPoints, setRadarPoints] = useState<RadarPoint[]>([]);
  const [radarPointsVisible, setRadarPointsVisible] = useState(true);
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState<DrawMode>('NONE');
  const [drawnPolygon, setDrawnPolygon] = useState<LngLat[] | null>(null);
  const [polygonResult, setPolygonResult] = useState<PolygonTimeSeriesResponse | null>(null);
  const [highlightedPointIds, setHighlightedPointIds] = useState<Set<string>>(new Set());
  const [busyState, setBusyState] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const setFilters = useCallback(
    (patch: Partial<AnalysisFilters>) =>
      setFiltersRaw((prev) => ({ ...prev, ...patch })),
    [],
  );

  const setBusy = useCallback(
    (key: string, v: boolean) =>
      setBusyState((prev) => ({ ...prev, [key]: v })),
    [],
  );

  return useMemo(
    () => ({
      filters,
      setFilters,
      scenes,
      setScenes,
      layer,
      setLayer,
      layerVisible,
      setLayerVisible,
      layerOpacity,
      setLayerOpacity,
      layerContrast,
      setLayerContrast,
      layerSaturation,
      setLayerSaturation,
      layerBrightness,
      setLayerBrightness,
      layerHue,
      setLayerHue,
      colorThresholdCmYr,
      setColorThresholdCmYr,
      colorMaxCmYr,
      setColorMaxCmYr,
      filterBelowThreshold,
      setFilterBelowThreshold,
      radarPoints,
      setRadarPoints,
      radarPointsVisible,
      setRadarPointsVisible,
      selectedPointId,
      setSelectedPointId,
      drawMode,
      setDrawMode,
      drawnPolygon,
      setDrawnPolygon,
      polygonResult,
      setPolygonResult,
      highlightedPointIds,
      setHighlightedPointIds,
      busy: busyState,
      setBusy,
      error,
      setError,
    }),
    [
      filters, setFilters, scenes, layer, layerVisible, layerOpacity,
      layerContrast, layerSaturation, layerBrightness, layerHue,
      colorThresholdCmYr, colorMaxCmYr, filterBelowThreshold,
      radarPoints, radarPointsVisible, selectedPointId, drawMode, drawnPolygon,
      polygonResult, highlightedPointIds, busyState, setBusy, error,
    ],
  );
}

